import React, { useEffect, useMemo } from "react";
import MaterialIcon from "../material-icon";
import { MaterialSymbol } from "@material-symbols/font-400";
import { IconWrapper, NavContainer, NavItem, NavItemLabel } from "./bottom-navigation-bar.styles";
import { useLocation } from "react-router-dom";
import { usePWA } from "../../context/PWAContext";
import { colorPalette } from "../../utils/theme";
import useCustomNavigate from "../../hooks/useCustomNavigate";
import { Capacitor } from "@capacitor/core";
import { useKeyboardOpen } from "../../hooks/useKeyboardOpen";
import useInvestorDetailStore from "../../stores/investorDetailStore";
import useTenantDetailStore from "../../stores/tenantDetailStore";
import { useTranslation } from "react-i18next";
import useTicketSystemEnabled from "../../hooks/useTicketSystemEnabled";

interface NavItemProps {
    icon: MaterialSymbol;
    label: string;
    link: string;
    /** Anchor for the one-time support announcement. */
    tour?: string;
}

const NavigationBottomBar: React.FC = () => {
    const navigate = useCustomNavigate();
    const { t } = useTranslation();
    const ticketsEnabled = useTicketSystemEnabled();
    const location = useLocation();
    const isPWA = usePWA();
    const isCapacitor = Capacitor.isNativePlatform();
    const isKeyboardOpen = useKeyboardOpen();
    // A request's conversation pins its reply box where the tabs would be.
    const isConversation = /^\/requests\/\d+/.test(location.pathname);

    // Stores
    const investor = useInvestorDetailStore(state => state.investor);
    const tenant = useTenantDetailStore(state => state.tenant);

    // Add a class to the body element when navigation bar is visible
    useEffect(() => {
        if ((isCapacitor || isPWA) && !isConversation) {
            const platform = Capacitor.getPlatform(); // "ios" | "android" | "web"
            document.body.classList.add('bottom-nav-visible');

            if (platform === 'ios') {
                document.body.classList.add('bottom-nav-ios');
            } else if (platform === 'android') {
                document.body.classList.add('bottom-nav-android');
            }
        }

        return () => {
            document.body.classList.remove('bottom-nav-visible');
            document.body.classList.remove('bottom-nav-ios');
            document.body.classList.remove('bottom-nav-android');
        };
    }, [isCapacitor, isPWA, isConversation]);

    const navItems: NavItemProps[] = useMemo(() => {
        if (investor) {
            return [
                { icon: "grid_view", label: t('tab_bar.landlord.dashboard'), link: "/dashboard" },
                { icon: "home_work", label: t('tab_bar.landlord.units'), link: "/my-units" },
                { icon: "table_chart_view", label: t('tab_bar.landlord.finances'), link: "/financials" },
                { icon: "description", label: t('tab_bar.landlord.contracts'), link: "/contracts" },
                // Support takes the fifth tab; the knowledge hub stays one tap
                // away through "Wissenswertes" on the dashboard.
                ticketsEnabled
                    ? { icon: "support_agent", label: t('tab_bar.landlord.requests'), link: "/requests", tour: "support-tab" }
                    : { icon: "school", label: t('tab_bar.landlord.knowledge_hub'), link: "/knowledge-hub" },
            ]
        } else {
            return [
                { icon: "grid_view", label: t('tab_bar.tenant.dashboard'), link: "/dashboard" },
                { icon: "home_work", label: t('tab_bar.tenant.unit'), link: "/my-unit" },
                { icon: "payments", label: t('tab_bar.tenant.payments'), link: "/payments" },
                ...(ticketsEnabled
                    ? [{ icon: "support_agent" as const, label: t('tab_bar.landlord.requests'), link: "/requests" }]
                    : []),
                //{ icon: "description", label: "Community", link: "/knowledge-hub" },
            ]
        }
    }, [t, investor, ticketsEnabled]);

    // Show navigation bar if mobile or Capacitor
    if (!isCapacitor && !isPWA) return (<></>);
    if (isConversation) return (<></>);
    if (isCapacitor && isKeyboardOpen) return (<></>);

    const handleNavigate = (link: string) => {
        navigate(link, { replace: true, clearHistory: true });
    };

    return (
        <NavContainer>
            {navItems.map((item, index) => {
                const active = location.pathname === item.link || location.pathname.startsWith(`${item.link}/`);
                return (
                    <NavItem
                        key={index}
                        data-tour={item.tour}
                        onClick={() => handleNavigate(item.link)}
                    >
                        <IconWrapper>
                            <MaterialIcon icon={item.icon} size={24} color={active ? colorPalette.blue500 : colorPalette.textMain} />
                        </IconWrapper>
                        <NavItemLabel active={active}>{item.label}</NavItemLabel>
                    </NavItem>
                );
            })}
        </NavContainer>
    );
};

export default NavigationBottomBar;
